
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useAuthActions } from "@/hooks/useAuthActions";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

export function RegisterForm() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsLoading(true);

    try {
      // Email validatie
      if (!email.includes('@')) {
        toast({
          title: "Ongeldig emailadres",
          description: "Voer een geldig emailadres in",
          variant: "destructive",
        });
        return;
      }

      // Wachtwoord validatie
      if (password.length < 6) {
        toast({
          title: "Wachtwoord te kort",
          description: "Je wachtwoord moet minimaal 6 tekens bevatten",
          variant: "destructive",
        });
        return;
      }

      // Registreer de gebruiker met Supabase
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            name,
          },
        },
      });

      if (error) {
        throw error;
      }

      // Voeg gebruiker toe aan ActiveCampaign lijst
      try {
        await supabase.functions.invoke("add-contact-to-activecampaign", {
          body: { 
            email,
            name
          }
        });
        console.log("Gebruiker toegevoegd aan ActiveCampaign lijst");
      } catch (acError) {
        console.error("Fout bij toevoegen aan ActiveCampaign:", acError);
        // We gaan door met registratie ook al faalt de AC toevoeging
      }

      // Succes melding
      toast({
        title: "Registratie succesvol",
        description: "Je account is aangemaakt. Controleer je e-mail voor de verificatielink.",
      });

      // Redirect naar login pagina
      setTimeout(() => {
        navigate("/auth/login");
      }, 2000);

    } catch (error) {
      console.error("Registration error:", error);
      toast({
        title: "Registratie mislukt",
        description: error.message || "Er is iets misgegaan. Probeer het opnieuw.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className="w-[350px]">
      <CardHeader>
        <CardTitle>Registreren</CardTitle>
        <CardDescription>
          Maak een nieuw account aan om te beginnen
        </CardDescription>
      </CardHeader>
      <form onSubmit={handleSubmit}>
        <CardContent>
          <div className="grid w-full items-center gap-4">
            <div className="flex flex-col space-y-1.5">
              <Input
                id="name"
                placeholder="Naam"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
            <div className="flex flex-col space-y-1.5">
              <Input
                id="email"
                placeholder="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="flex flex-col space-y-1.5">
              <Input
                id="password"
                placeholder="Wachtwoord"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
          </div>
        </CardContent>
        <CardFooter className="flex justify-between">
          <Button
            variant="outline"
            onClick={() => navigate("/auth/login")}
            disabled={isLoading}
          >
            Annuleren
          </Button>
          <Button type="submit" disabled={isLoading}>
            {isLoading ? "Bezig..." : "Registreren"}
          </Button>
        </CardFooter>
      </form>
    </Card>
  );
}
